import React, { Suspense, useDeferredValue, useMemo, useState, useEffect } from 'react';
import { Appointment, Service, Venue, AddOn, PricingTier, Payment } from '@/types';
import {
  useAppointments,
  DataErrorBoundary,
  type AppointmentListParams
} from '@features/admin/data';
import {
  Download,
  Filter,
  PoundSterling,
  CreditCard,
  FileText,
  RefreshCcw,
  Printer,
  Search,
  ArrowDown,
  ArrowUp,
  Loader2
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { Select } from '@shared/components/ui/Select';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { toMoneyNumber } from '@/lib/formatters';
import { formatUkDate, formatUkDateTime } from '@/lib/dates';
import { api } from '@/services/api';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { buildMapById } from '@/lib/collections';
import { getAppointmentServiceIds, getAppointmentServiceNames } from '@/lib/appointmentServices';
import { FinanceList } from './FinanceList';
import { PaymentDetailModal, formatAmount } from './PaymentDetailModal';

interface FinanceTabProps {
  services: Service[];
  addOns: AddOn[];
  venues: Venue[];
}

export const FinanceTab: React.FC<FinanceTabProps> = ({ services, addOns, venues }) => {
  const [loadedAppointments, setLoadedAppointments] = useState<Appointment[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalAppointment, setPaymentModalAppointment] = useState<Appointment | null>(null);
  const [paymentModalRecord, setPaymentModalRecord] = useState<Payment | null>(null);
  const [paymentModalLoading, setPaymentModalLoading] = useState(false);
  const [paymentModalError, setPaymentModalError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [dateSortDir, setDateSortDir] = useState<'asc' | 'desc'>('desc');
  const servicesById = useMemo(() => buildMapById(services), [services]);
  const addOnsById = useMemo(() => buildMapById(addOns), [addOns]);
  const venuesById = useMemo(() => buildMapById(venues), [venues]);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [venueFilter, setVenueFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [poSearch, setPoSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const deferredPoSearch = useDeferredValue(poSearch);

  const buildBookingParams = ({
    page: pageOverride,
    pageSize: pageSizeOverride,
    isExportCsv = false
  }: { page?: number; pageSize?: number; isExportCsv?: boolean } = {}): AppointmentListParams => ({
    ...(!isExportCsv && { page: pageOverride ?? page }),
    ...(!isExportCsv && { pageSize: pageSizeOverride ?? pageSize }),
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    venueId: venueFilter === 'all' ? undefined : venueFilter,
    status:
      statusFilter === 'all'
        ? undefined
        : statusFilter === 'billable'
          ? 'completed,noshow'
          : statusFilter,
    paymentMethod: paymentFilter === 'all' ? undefined : paymentFilter,
    paymentStatus: paymentStatusFilter === 'all' ? undefined : paymentStatusFilter,
    poNumber: deferredPoSearch || undefined,
    q: deferredSearch || undefined,
    sortBy: 'date',
    sortDir: dateSortDir
  });

  useEffect(() => {
    setPage(1);
  }, [
    dateFrom,
    dateTo,
    venueFilter,
    statusFilter,
    paymentFilter,
    paymentStatusFilter,
    search,
    poSearch,
    dateSortDir
  ]);

  const formatDate = (value?: string | Date | null) => formatUkDate(value);
  const formatDateTime = (value?: string | Date | null) => formatUkDateTime(value);

  const formatPaymentStatusLabel = (status?: string) => {
    const normalized = String(status || '').trim();
    if (!normalized) return 'Unknown';
    return normalized
      .split('_')
      .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ''))
      .join(' ')
      .trim();
  };

  const getReminderMeta = (status?: Appointment['reminderStatus']) => {
    const normalized = status || 'pending';
    if (normalized === 'sent') return { label: 'Sent', variant: 'success' as const };
    if (normalized === 'failed') return { label: 'Failed', variant: 'danger' as const };
    if (normalized === 'skipped') return { label: 'Skipped', variant: 'neutral' as const };
    return { label: 'Pending', variant: 'neutral' as const };
  };

  const getPaymentStatusMeta = (method: Appointment['paymentMethod'], status?: string) => {
    const normalized = String(status || '')
      .trim()
      .toLowerCase();
    if (method === 'invoice') return { label: 'Invoiced', variant: 'info' as const };
    if (!normalized) return { label: 'Not Recorded', variant: 'warning' as const };
    if (normalized === 'captured') return { label: 'Captured', variant: 'success' as const };
    if (normalized === 'authorized') return { label: 'Authorized', variant: 'info' as const };
    if (normalized === 'pending_3ds') return { label: 'Pending 3DS', variant: 'warning' as const };
    if (normalized === 'failed') return { label: 'Failed', variant: 'danger' as const };
    return { label: formatPaymentStatusLabel(normalized), variant: 'neutral' as const };
  };

  const hasInvoicePaymentAnomaly = (app: Appointment) =>
    app.paymentMethod === 'invoice' && app.invoicePaymentAnomaly === true;

  const getPaymentRef = (app: Appointment) =>
    app.paymentMethod === 'invoice'
      ? ''
      : app.paymentTransactionId || app.paymentVendorTxCode || app.paymentId || '';

  const formatPaymentRef = (app: Appointment) => {
    const raw = getPaymentRef(app);
    if (!raw) return '';
    const value = String(raw);
    if (value.length <= 14) return value;
    return `${value.slice(0, 6)}...${value.slice(-4)}`;
  };

  const calculateBookingCost = (app: Appointment) => {
    if (app.totalAmount != null) return toMoneyNumber(app.totalAmount);
    const tier: PricingTier = 'bronze';
    let total = 0;
    getAppointmentServiceIds(app).forEach((serviceId) => {
      const service = servicesById.get(serviceId);
      if (service) total += toMoneyNumber(service.tierPrices?.[tier] ?? service.price);
    });
    (app.addOnIds || []).forEach((id) => {
      const addon = addOnsById.get(id);
      if (addon) total += toMoneyNumber(addon.tierPrices?.[tier] ?? addon.price);
    });
    return total;
  };

  const toggleDateSort = () => setDateSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  const renderDateSortIcon = () =>
    dateSortDir === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-slate-600" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-slate-600" />
    );

  const displayAppointments =
    statusFilter === 'billable'
      ? loadedAppointments.filter((app) => app.status === 'completed' || app.status === 'noshow')
      : loadedAppointments;

  const totalRevenue = displayAppointments.reduce((sum, app) => sum + calculateBookingCost(app), 0);
  const totalInvoiced = displayAppointments
    .filter((a) => a.paymentMethod === 'invoice')
    .reduce((sum, app) => sum + calculateBookingCost(app), 0);
  const totalCard = displayAppointments
    .filter((a) => a.paymentMethod === 'card')
    .reduce((sum, app) => sum + calculateBookingCost(app), 0);

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setPaymentModalAppointment(null);
    setPaymentModalRecord(null);
    setPaymentModalError('');
    setPaymentModalLoading(false);
  };

  const openPaymentModal = async (appointment: Appointment) => {
    setPaymentModalAppointment(appointment);
    setPaymentModalOpen(true);
    setPaymentModalRecord(null);
    setPaymentModalError('');
    setPaymentModalLoading(true);
    try {
      const data = await api.listStaffPayments({
        page: 1,
        pageSize: 1,
        appointmentId: appointment.id
      });
      const record = data.items?.[0] || null;
      setPaymentModalRecord(record);
      if (!record && appointment.paymentMethod === 'card') {
        setPaymentModalError('No payment record found for this booking.');
      }
    } catch (err: unknown) {
      setPaymentModalError(normalizeErrorMessage(err, 'Failed to load payment details.'));
    } finally {
      setPaymentModalLoading(false);
    }
  };

  const fetchAllBookingsForExport = async () => {
    const pageSizeForExport = 100;
    let currentPage = 1;
    let totalCountForExport = 0;
    let items: Appointment[] = [];
    while (true) {
      const exportParams = buildBookingParams({ page: currentPage, pageSize: pageSizeForExport });
      const data = await api.listStaffBookings(
        exportParams as unknown as Record<string, string | number | boolean | undefined>
      );
      const pageItems = data.items || [];
      items = items.concat(pageItems);
      totalCountForExport = Number(data.totalCount || 0);
      if (pageItems.length === 0 || items.length >= totalCountForExport) break;
      currentPage += 1;
    }
    return items;
  };

  const handlePrint = async () => {
    const printWindow = window.open('', '', 'height=800,width=1000');
    if (!printWindow) return;
    printWindow.document.write(
      `<!DOCTYPE html><html><head><title>Finance Report</title></head><body><p>Preparing report...</p></body></html>`
    );
    printWindow.document.close();
    try {
      const allAppointments = await fetchAllBookingsForExport();
      const exportAppointments =
        statusFilter === 'billable'
          ? allAppointments.filter((app) => app.status === 'completed' || app.status === 'noshow')
          : allAppointments;
      const totalR = exportAppointments.reduce((sum, app) => sum + calculateBookingCost(app), 0);
      const totalI = exportAppointments
        .filter((a) => a.paymentMethod === 'invoice')
        .reduce((sum, app) => sum + calculateBookingCost(app), 0);
      const totalC = exportAppointments
        .filter((a) => a.paymentMethod === 'card')
        .reduce((sum, app) => sum + calculateBookingCost(app), 0);

      const htmlContent = `<!DOCTYPE html><html><head><title>Finance Report - ${formatDate(new Date())}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 20px; }
          h1 { font-size: 24px; margin-bottom: 10px; }
          .meta { font-size: 12px; color: #666; margin-bottom: 20px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
          .summary { display: flex; gap: 20px; margin-bottom: 20px; }
          .summary-box { border: 1px solid #ddd; padding: 10px; border-radius: 4px; min-width: 150px; }
          .summary-label { font-size: 10px; color: #666; text-transform: uppercase; font-weight: bold; }
          .summary-value { font-size: 18px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f8f9fa; font-weight: 600; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .text-right { text-align: right; }
          @media print { body { padding: 0; } }
        </style>
      </head><body>
        <h1>Finance Report</h1>
        <div class="meta">Generated on: ${formatDateTime(new Date())}<br/>Filters Applied: ${venueFilter !== 'all' ? 'Venue Filter' : 'All Venues'}, ${statusFilter !== 'all' ? statusFilter : 'All Statuses'}</div>
        <div class="summary">
          <div class="summary-box"><div class="summary-label">Total Revenue</div><div class="summary-value">£${totalR.toFixed(2)}</div></div>
          <div class="summary-box"><div class="summary-label">Invoiced</div><div class="summary-value">£${totalI.toFixed(2)}</div></div>
          <div class="summary-box"><div class="summary-label">Card Payments</div><div class="summary-value">£${totalC.toFixed(2)}</div></div>
        </div>
        <table><thead><tr>
          <th>Date</th><th>Time</th><th>Ref</th><th>PO Number</th><th>Customer</th><th>Email</th>
          <th>Booked By Name</th><th>Booked By Email</th><th>Company</th><th>Service</th>
          <th>Add-ons</th><th>Venue</th><th>Status</th><th>Reminder</th><th>Method</th>
          <th>Payment Status</th><th>Payment Ref</th><th>Payment Provider</th><th>Captured At</th>
          <th class="text-right">Amount</th>
        </tr></thead><tbody>
          ${exportAppointments
            .map((app) => {
              const serviceNames = getAppointmentServiceNames(app, servicesById);
              const venue = venuesById.get(app.venueId);
              const addonNames = (app.addOnIds || [])
                .map((id) => addOnsById.get(id)?.name)
                .filter(Boolean)
                .join(', ');
              const cost = calculateBookingCost(app);
              return `<tr>
              <td>${formatDate(app.date)}</td><td>${app.time || '-'}</td><td>${app.id}</td>
              <td>${app.poNumber || '-'}</td><td>${app.customerName}</td><td>${app.customerEmail || '-'}</td>
              <td>${app.bookedByName || '-'}</td><td>${app.bookedByEmail || '-'}</td><td>${app.companyName || '-'}</td>
              <td>${serviceNames || 'Unknown'}</td><td>${addonNames || '-'}</td>
              <td>${venue ? venue.name : 'Unknown'}</td><td>${app.status}</td>
              <td>${getReminderMeta(app.reminderStatus).label}</td><td>${app.paymentMethod}</td>
              <td>${getPaymentStatusMeta(app.paymentMethod, app.paymentStatus).label}</td>
              <td>${getPaymentRef(app) || '-'}</td>
              <td>${app.paymentMethod === 'invoice' ? '' : app.paymentProvider || '-'}</td>
              <td>${app.paymentMethod === 'invoice' || !app.paymentCapturedAt ? '-' : formatDateTime(app.paymentCapturedAt)}</td>
              <td class="text-right">£${cost.toFixed(2)}</td>
            </tr>`;
            })
            .join('')}
        </tbody></table>
        <script>window.onload = function() { window.print(); }</script>
      </body></html>`;

      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to generate finance report.'));
      try {
        printWindow.close();
      } catch (_) {
        /* ignore */
      }
    }
  };

  const handleExportAllRowsCSV = async () => {
    try {
      setIsExporting(true);
      const exportParams = buildBookingParams({ isExportCsv: true });
      const data = await api.listStaffAllBookings(
        exportParams as unknown as Record<string, string | number | boolean | undefined>
      );

      const csv = typeof data === 'string' ? data : JSON.stringify(data);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `finance_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to export finance report.'));
    } finally {
      setIsExporting(false);
    }
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setVenueFilter('all');
    setStatusFilter('all');
    setPaymentFilter('all');
    setPaymentStatusFilter('all');
    setSearch('');
    setPoSearch('');
  };

  return (
    <div className="animate-fade-in space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">Total Value</p>
            <h3 className="text-2xl font-bold text-slate-900 mt-1">£{totalRevenue.toFixed(2)}</h3>
            <p className="text-xs text-slate-400 mt-1">{displayAppointments.length} bookings</p>
          </div>
          <div className="bg-emerald-50 p-3 rounded-full text-emerald-600">
            <PoundSterling className="w-6 h-6" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">On Account (Invoiced)</p>
            <h3 className="text-2xl font-bold text-slate-900 mt-1">£{totalInvoiced.toFixed(2)}</h3>
          </div>
          <div className="bg-blue-50 p-3 rounded-full text-blue-600">
            <FileText className="w-6 h-6" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">Paid via Card</p>
            <h3 className="text-2xl font-bold text-slate-900 mt-1">£{totalCard.toFixed(2)}</h3>
          </div>
          <div className="bg-purple-50 p-3 rounded-full text-purple-600">
            <CreditCard className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <Filter className="w-4 h-4 text-teal-600" /> Filter Report
          </h3>
          <div className="flex gap-2">
            <Button
              onClick={clearFilters}
              variant="ghost"
              size="sm"
              className="px-0 text-slate-500 hover:text-red-600"
            >
              <RefreshCcw className="w-3 h-3" /> Reset
            </Button>
            <Button onClick={handlePrint} variant="outline" size="sm">
              <Printer className="w-4 h-4" /> Print / PDF
            </Button>
            <Button
              onClick={handleExportAllRowsCSV}
              variant="dark"
              size="sm"
              disabled={isExporting}
            >
              <Download className="w-4 h-4" /> {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Date From</label>
            <Input
              type="date"
              className="focus:ring-1"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Date To</label>
            <Input
              type="date"
              className="focus:ring-1"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Location</label>
            <Select
              className="focus:ring-1"
              value={venueFilter}
              onChange={(e) => setVenueFilter(e.target.value)}
            >
              <option value="all">All Locations</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Status</label>
            <Select
              className="focus:ring-1"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="billable">Billable Only (Completed/NoShow)</option>
              <option value="completed">Completed</option>
              <option value="noshow">No Show</option>
              <option value="booked">Booked (Forecast)</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Payment Status</label>
            <Select
              className="focus:ring-1"
              value={paymentStatusFilter}
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
            >
              <option value="all">All Payment Statuses</option>
              <option value="captured">Captured</option>
              <option value="authorized">Authorized</option>
              <option value="pending_3ds">Pending 3DS</option>
              <option value="failed">Failed</option>
              <option value="none">Not Recorded</option>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">General Search</label>
            <Input
              placeholder="Ref, Name, Email, Company, or Opayo Ref..."
              className="focus:ring-1"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">PO Number</label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <Input
                placeholder="Search PO..."
                className="pl-7 focus:ring-1"
                value={poSearch}
                onChange={(e) => setPoSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <DataErrorBoundary invalidateOnRetry="appointments">
        <Suspense
          fallback={
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
              Loading finance data...
            </div>
          }
        >
          <FinanceList
            params={buildBookingParams({ page, pageSize })}
            statusFilter={statusFilter}
            servicesById={servicesById}
            venuesById={venuesById}
            addOnsById={addOnsById}
            calculateBookingCost={calculateBookingCost}
            getReminderMeta={getReminderMeta}
            getPaymentStatusMeta={getPaymentStatusMeta}
            formatPaymentRef={formatPaymentRef}
            formatDate={formatDate}
            formatDateTime={formatDateTime}
            hasInvoicePaymentAnomaly={hasInvoicePaymentAnomaly}
            toggleDateSort={toggleDateSort}
            renderDateSortIcon={renderDateSortIcon}
            onOpenPaymentModal={openPaymentModal}
            onItemsLoaded={setLoadedAppointments}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </Suspense>
      </DataErrorBoundary>

      <PaymentDetailModal
        open={paymentModalOpen}
        onClose={closePaymentModal}
        appointment={paymentModalAppointment}
        record={paymentModalRecord}
        isLoading={paymentModalLoading}
        error={paymentModalError}
        getPaymentStatusMeta={getPaymentStatusMeta}
        hasInvoicePaymentAnomaly={hasInvoicePaymentAnomaly}
        formatAmount={formatAmount}
        formatDateTime={formatDateTime}
      />
    </div>
  );
};
